import React, { PropsWithChildren, useEffect, useState } from 'react';
import { FilterEventHandler, IBootstrapTableColumnProps } from 'react-bootstrap-table-next';
import { INumberInputProps, NumberInput } from '../NumberInput';
import { useSortEventListenerFix } from './useSortEventListenerFix';
import { useDelayedOnFilterEffect } from './useDelayedOnFilterEffect';
import { ICustomNumberFilterProps } from 'react-bootstrap-table2-filter';
import { convertNullToUndefined } from '../../utilities/convertNullToUndefined';
import { IconType } from '../Icon';

export interface INumberFilterProps<T extends object = {}> extends ICustomNumberFilterProps {
  onFilter: FilterEventHandler;
  column: IBootstrapTableColumnProps<T>;
}

export const NumberFilter = <T extends object = {}>({
  defaultValue,
  delay,
  onFilter,
  column,
  type,
  children,
  ...rest
}: PropsWithChildren<INumberFilterProps<T>>) => {
  //FYI: preventing event bubbling because BootstrapTable sorts when focusing filter elements
  const clickEventInputRef = useSortEventListenerFix();
  const [value, setValue] = useState<number | undefined>(defaultValue);
  const config: INumberInputProps =
    rest['mode'] === 'currency' ? { decimalScale: 2, fixedDecimalScale: true, ...rest } : rest;

  useEffect(() => setValue(defaultValue), [defaultValue]);

  const isDelaying = useDelayedOnFilterEffect(value, delay, onFilter);

  return (
    <NumberInput
      {...config}
      appendIconType={!!delay && isDelaying ? IconType.Loading : undefined}
      getInputRef={clickEventInputRef}
      onChange={(newValue) => setValue(convertNullToUndefined(newValue))}
      value={value}
    />
  );
};

export const NumberFilterRenderer =
  <T extends object>(config: ICustomNumberFilterProps) =>
  (onFilter: FilterEventHandler, column: IBootstrapTableColumnProps<T>) => (
    <NumberFilter {...config} column={column} onFilter={onFilter} />
  );
